import type { Theme } from '@mui/material'
import type { FC } from 'react'
import type { PeriodOverviewsResponse } from 'types/dashboard'
import { CheckCircleFilled, WarningFilled } from '@ant-design/icons'
import { Box, Grid2 as Grid, Typography } from '@mui/material'
import { ThemeMode } from 'types/config'

type OnboardOverviewsProps = Pick<
  PeriodOverviewsResponse,
  'planApprovalStats' | 'targetMetricCounts' | 'approvedComponentCounts' | 'accrualCounts'
> & {
  theme: Theme
}

const OnboardOverviews: FC<OnboardOverviewsProps> = ({
  planApprovalStats,
  approvedComponentCounts,
  accrualCounts,
  theme,
}) => {
  const renderStatusIcon = (value: number, total: number, isAccrual: boolean = false) => {
    if (isAccrual && total === 0) {
      return <Box sx={{ mr: 1.75 }} />
    } else if (isAccrual && value === 0 && total === 0) {
      return <CheckCircleFilled style={{ color: 'green' }} />
    } else if (value > 0 && value === total) {
      return <CheckCircleFilled style={{ color: 'green' }} />
    } else {
      return <WarningFilled style={{ color: 'orange' }} />
    }
  }

  const renderSingleStatusIcon = (value: number) => {
    if (value > 0) {
      return <WarningFilled style={{ color: 'orange' }} />
    } else {
      return <CheckCircleFilled style={{ color: 'green' }} />
    }
  }

  const renderCountItem = (label: string, value: number, total: number, isAccrual: boolean = false) => (
    <Grid size={{ xs: 12 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" width="100%">
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {label}
          </Typography>
          <Box sx={{ width: '80px', textAlign: 'right', mr: 2 }}>
            <Typography variant="body1">{`${value}/${total}`}</Typography>
          </Box>
        </Box>
        {renderStatusIcon(value, total, isAccrual)}
      </Box>
    </Grid>
  )

  const renderSingleCountItem = (label: string, total: number) => (
    <Grid size={{ xs: 12 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" width="100%">
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {label}
          </Typography>
          <Box sx={{ width: '80px', textAlign: 'right', mr: 2 }}>
            <Typography variant="body1">{`${total}`}</Typography>
          </Box>
        </Box>
        {renderSingleStatusIcon(total)}
      </Box>
    </Grid>
  )
  const dividerColor = theme.palette.mode === ThemeMode.DARK ? theme.palette.grey[800] : theme.palette.grey[300]

  const renderDivider = () => (
    <Box
      sx={{
        width: '100%',
        height: '1px',
        backgroundColor: dividerColor,
        my: 0.25,
      }}
    />
  )

  const titleColor = theme.palette.mode === ThemeMode.DARK ? 'primary' : 'primary'

  const accrualsDraft = accrualCounts != null ? accrualCounts.draftCount : 0
  const accrualsPending = accrualCounts != null ? accrualCounts.pendingCount : 0
  const accrualsApproved = accrualCounts != null ? accrualCounts.approvedCount : 0
  return (
    <Box sx={{ mb: 1, padding: 2 }}>
      <Typography fontSize={14} fontWeight={500} color={titleColor} sx={{ mb: 2 }}>
        Status Overview
      </Typography>
      <Grid container spacing={1} sx={{ mb: 3 }}>
        {renderCountItem('Employee Plans Approved', planApprovalStats.approvedUsers, planApprovalStats.totalUsers)}
        {renderDivider()}
        {renderCountItem(
          'Targets Approved',
          approvedComponentCounts.approvedTargets,
          approvedComponentCounts.totalTargets
        )}
        {renderDivider()}
        {renderCountItem(
          'Actuals Reported',
          approvedComponentCounts.totalMetrics,
          approvedComponentCounts.approvedTargets
        )}
        {renderDivider()}
        {renderCountItem(
          'Actuals Approved',
          approvedComponentCounts.approvedMetrics,
          approvedComponentCounts.totalMetrics
        )}
        {renderDivider()}
        {renderSingleCountItem('Accruals Ready', accrualsDraft)}
        {renderDivider()}
        {renderCountItem('Accruals Approved', accrualsApproved + accrualsPending, accrualsApproved)}
      </Grid>
      <Typography variant="caption" sx={{ mt: 2, display: 'block' }} color="text.secondary">
        * Actuals are due after the end date of the period of measurement.
      </Typography>
    </Box>
  )
}

export default OnboardOverviews
